// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import { questionEndpoints } from '@/modules/question/question.constants'
import type {
  TCreateQuestionRequest,
  TEditQuestionRequest,
  TQuestion,
  TQuestionDetail,
  TUploadQuestionsRequest,
} from '@/modules/question/question.types'

export async function getQuestionList(
  params: TAdminListParams
): Promise<TResponse<TQuestion[]>> {
  const response = await api.get<TResponse<TQuestion[]>>(
    questionEndpoints.list(params)
  )

  return response.data
}

export async function getQuestionDetail(id: string): Promise<TQuestionDetail> {
  const response = await api.get<TResponse<TQuestionDetail>>(
    questionEndpoints.detail(id)
  )

  return response.data.data
}

export async function createQuestion(
  payload: TCreateQuestionRequest
): Promise<void> {
  await api.post<TResponse<TQuestion>>(questionEndpoints.create, payload)
}

export async function uploadQuestions(
  payload: TUploadQuestionsRequest
): Promise<void> {
  const formData = new FormData()

  formData.append('file', payload.file)
  formData.append('category_id', payload.category_id)
  if (payload.subcategory_id) {
    formData.append('subcategory_id', payload.subcategory_id)
  }
  payload.match_modes.map((matchMode) => {
    formData.append('match_modes', matchMode)
  })
  formData.append('difficulty', payload.difficulty)
  if (payload.question_pool_id) {
    formData.append('question_pool_id', payload.question_pool_id)
  }

  await api.post<TResponse<TQuestion>>(questionEndpoints.import, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export async function editQuestion(
  payload: TEditQuestionRequest
): Promise<void> {
  const { id, question_pool_id: questionPoolId, ...body } = payload
  // The backend can only tell "clear subcategory_id" apart from "leave it
  // alone" via an explicit JSON `null` — omitting the key (what
  // JSON.stringify does for `undefined`) means "don't touch it".
  await api.patch(questionEndpoints.edit(id), {
    ...body,
    subcategory_id: body.subcategory_id ?? null,
    ...(questionPoolId !== undefined
      ? { question_pool_id: questionPoolId }
      : {}),
  })
}

export async function deleteQuestion(id: string): Promise<void> {
  await api.delete(questionEndpoints.delete(id))
}
